import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BannerData } from 'interfaces';

export const useBannersDetails = () => {
   const { t } = useTranslation();

   const SUPPLIER_ORDER_DETAILS_BANNERS: { [key: string]: BannerData } = useMemo(
      () => ({
         CANCEL: {
            variant: 'error',
            description: t('banners.supplier_order_details.cancel'),
         },
         REFUSE: {
            variant: 'error',
            description: t('banners.supplier_order_details.refuse'),
         },
         ACCEPT: {
            variant: 'success',
            title: t('banners.supplier_order_details.accept_title'),
            description: t('banners.supplier_order_details.accept_description'),
         },
         TO_PAY: {
            variant: 'success',
            description: t('banners.supplier_order_details.to_pay'),
         },
         ENDED: {
            variant: 'success',
            description: t('banners.supplier_order_details.ended'),
         },
         SUBMIT_FOR_BILLING: {
            variant: 'success',
            title: t('banners.supplier_order_details.submit_for_billing_title'),
            description: t('banners.supplier_order_details.submit_for_billing_description'),
         },
         ADD_ADDITIONAL_CHARGES: {
            variant: 'success',
            description: t('banners.supplier_order_details.add_additional_charges'),
         },
         SEND_PALLETS: {
            variant: 'success',
            description: t('banners.supplier_order_details.send_pallets'),
         },
         EDIT_SEND_PALLETS: {
            variant: 'success',
            description: t('banners.supplier_order_details.edit_send_pallets'),
         },
         SEND_DAMAGED_PALLETS: {
            variant: 'success',
            title: t('banners.supplier_order_details.send_damaged_pallets_title'),
            description: t('banners.supplier_order_details.send_damaged_pallets_description'),
         },
         REPORT_LOST_DESTROYED_PALLETS_BY_CLIENT: {
            variant: 'success',
            title: t(
               'banners.supplier_order_details.report_lost_destroyed_pallets_by_client_title',
            ),
            description: t(
               'banners.supplier_order_details.report_lost_destroyed_pallets_by_client_description',
            ),
         },
         REPORT_LOST_DESTROYED_PALLETS_BY_SUPPLIER: {
            variant: 'success',
            title: t(
               'banners.supplier_order_details.report_lost_destroyed_pallets_by_client_title',
            ),
            description: t(
               'banners.supplier_order_details.report_lost_destroyed_pallets_by_supplier_description',
            ),
         },
      }),
      [t],
   );

   const CLIENT_ORDER_DETAILS_BANNERS = useMemo(
      () =>
         ({
            REPORT_LOST_DESTROYED_PALLETS_BY_CLIENT: {
               variant: 'success',
               title: t('banners.client_order_details.report_lost_destroyed_by_client_title'),
               description: t(
                  'banners.client_order_details.report_lost_destroyed_by_client_description',
               ),
            },
            REPORT_LOST_DESTROYED_PALLETS_BY_SUPPLIER: {
               variant: 'success',
               title: t('banners.client_order_details.report_lost_destroyed_by_client_title'),
            },
            SEND_FOR_SETTLEMENT: {
               variant: 'success',
               title: t('banners.supplier_order_details.submit_for_billing_title'),
               description: t('banners.client_order_details.send_for_settlement_description'),
            },
            SEND_PALLETS: {
               variant: 'success',
               description: t('banners.supplier_order_details.send_pallets'),
            },
            SEND_DAMAGED_PALLETS: {
               variant: 'success',
               title: t('banners.supplier_order_details.send_damaged_pallets_title'),
               description: t('banners.supplier_order_details.send_damaged_pallets_description'),
            },
            EDIT_INTERNAL_ORDER_ID_SUCCESS: {
               variant: 'success',
               description: t('banners.client_order_details.edit_internal_order_id_success'),
            },
         } as const),
      [t],
   );

   const ORDER_DOCUMENTS_BANNERS = useMemo(
      () =>
         ({
            ADD_DOCUMENT_SUCCESS: {
               variant: 'success',
               description: t('banners.order_documents.add_document_success'),
            },
            ADD_DOCUMENT_ERROR: {
               variant: 'error',
               description: t('banners.order_documents.add_document_error'),
            },
            REMOVE_DOCUMENT_SUCCESS: {
               variant: 'success',
               description: t('banners.order_documents.remove_document_success'),
            },
            REMOVE_DOCUMENT_ERROR: {
               variant: 'error',
               description: t('banners.order_documents.remove_document_error'),
            },
            FETCH_DOCUMENTS_ERROR: {
               variant: 'error',
               description: t('banners.order_documents.fetch_documents_error'),
            },
         } as const),
      [t],
   );

   const COMMERCIAL_NETWORK_PAGE_BANNERS: { [key: string]: BannerData } = useMemo(
      () => ({
         ADD_DISTRIBUTION_CENTER_SUCCESS: {
            variant: 'success',
            description: t('banners.commercial_network_page.add_distribution_center_success'),
            withCloseIcon: true,
         },
         DELETE_COMMERCIAL_NETWORK: {
            variant: 'success',
            withCloseIcon: true,
            description: t('banners.commercial_network_page.delete_commercial_network'),
         },
         DELETE_DISTRIBUTION_CENTER: {
            variant: 'success',
            withCloseIcon: true,
            description: t('banners.commercial_network_page.delete_distribution_center'),
         },
      }),
      [t],
   );

   const SUPPLIER_ORDERS_DASHBOARD_BANNERS: { [key: string]: BannerData } = useMemo(
      () => ({
         SUCCESS_PLACE_ORDER: {
            variant: 'success',
            title: t('banners.supplier_orders_dashboard.success_place_order_title'),
            description: t('banners.supplier_orders_dashboard.success_place_order_description'),
         },
         SUCCESS_REMOVE_ORDER: {
            variant: 'error',
            description: t('banners.supplier_orders_dashboard.success_remove_order'),
         },
      }),
      [t],
   );

   const ADD_OR_EDIT_CLIENT_PAGE_BANNERS = useMemo(
      () =>
         ({
            ADD_COMMERCIAL_NETWORK_SUCCESS: {
               variant: 'success',
               description: t('banners.add_or_edit_client_page.add_commercial_network_success'),
            },
            ADD_DISTRIBUTION_CENTER_SUCCESS: {
               variant: 'success',
               description: t('banners.commercial_network_page.add_distribution_center_success'),
            },
         } as const),
      [t],
   );

   const DISTRIBUTION_CENTER_PAGE_BANNERS: { [key: string]: BannerData } = useMemo(
      () => ({
         TRANSFER_PALLETS_SUCCESS: {
            variant: 'success',
            description: t('banners.distribution_center_page.transfer_pallets_success'),
            withCloseIcon: true,
         },
         ADMISSION_PALLETS: {
            variant: 'success',
            withCloseIcon: true,
            description: t('banners.distribution_center_page.admission_pallets'),
         },
      }),
      [t],
   );

   const PALLET_PICK_UP_PAGE_BANNERS = useMemo(
      () =>
         ({
            ADD_SUCCESS: {
               variant: 'success',
               description: t('banners.pallet_pick_up_page.add_success'),
            },
         } as const),
      [t],
   );

   const EDIT_USER_BALANCE_BANNER = useMemo(
      () =>
         ({
            ADD_SUCCESS: {
               variant: 'success',
               description: t('distribution_center_pallets.success_banner'),
            },
         } as const),
      [t],
   );

   const LOGOUT_ERROR_BANNER = useMemo(
      () =>
         ({
            variant: 'error',
            description: t('banners.logout_error.description'),
         } as const),
      [t],
   );

   const PALLETS_TRANSFER_HISTORY_PAGE_BANNERS = useMemo(
      () =>
         ({
            GET_DATA_IN_XLSX_FORMAT_SUCCESS: {
               variant: 'success',
               description: t(
                  'banners.pallets_transfer_history_page.get_data_in_xlsx_format_success',
               ),
               withCloseIcon: true,
            },
            GET_DATA_IN_XLSX_FORMAT_ERROR: {
               variant: 'error',
               description: t(
                  'banners.pallets_transfer_history_page.get_data_in_xlsx_format_error',
               ),
               withCloseIcon: true,
            },
         } as const),
      [t],
   );

   const SUPPLIER_PALLETS_PICK_UPS_PAGE_BANNERS: Record<string, BannerData> = useMemo(
      () => ({
         ACCEPT_PALLET_PICK_UP_SUCCESS: {
            variant: 'success',
            description: t('banners.supplier_pallets_pick_ups_page.accept_pallet_pick_up_success'),
            withCloseIcon: true,
         },
         REJECT_PALLET_PICK_UP_SUCCESS: {
            variant: 'error',
            description: t('banners.supplier_pallets_pick_ups_page.reject_pallet_pick_up_success'),
            withCloseIcon: true,
         },
      }),
      [t],
   );

   return {
      ORDER_DOCUMENTS_BANNERS,
      SUPPLIER_ORDER_DETAILS_BANNERS,
      CLIENT_ORDER_DETAILS_BANNERS,
      COMMERCIAL_NETWORK_PAGE_BANNERS,
      SUPPLIER_ORDERS_DASHBOARD_BANNERS,
      ADD_OR_EDIT_CLIENT_PAGE_BANNERS,
      DISTRIBUTION_CENTER_PAGE_BANNERS,
      PALLET_PICK_UP_PAGE_BANNERS,
      EDIT_USER_BALANCE_BANNER,
      LOGOUT_ERROR_BANNER,
      PALLETS_TRANSFER_HISTORY_PAGE_BANNERS,
      SUPPLIER_PALLETS_PICK_UPS_PAGE_BANNERS,
   };
};
